import { useEffect, useState } from 'react';

const FAIL_OPEN_WARNING = 'Company policy acknowledgement check is temporarily unavailable. The portal remains open; please notify admin if this persists.';

export default function useAgentPolicyGate({ currentUser, isActualAdmin, isProcessing }) {
    const [status, setStatus] = useState(null);
    const [warning, setWarning] = useState('');
    const [error, setError] = useState('');
    const [acknowledging, setAcknowledging] = useState(false);

    useEffect(() => {
        if (!currentUser || isActualAdmin || isProcessing) {
            setStatus(null);
            setWarning('');
            setError('');
            return undefined;
        }

        let ignore = false;
        const loadStatus = async () => {
            try {
                const response = await fetch('/api/agent-dashboard/policy/status');
                const data = await response.json();
                if (ignore) return;
                if (!response.ok || !data.success) {
                    throw new Error(data.error || 'Policy status unavailable.');
                }
                setStatus(data.status || null);
                setWarning('');
                setError('');
            } catch (loadError) {
                if (!ignore) {
                    setStatus(null);
                    setWarning(FAIL_OPEN_WARNING);
                }
            }
        };

        loadStatus();
        return () => { ignore = true; };
    }, [currentUser, isActualAdmin, isProcessing]);

    const acknowledge = async () => {
        setAcknowledging(true);
        setError('');
        try {
            const response = await fetch('/api/agent-dashboard/policy/acknowledge', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to acknowledge policy.');
            }
            setStatus(data.status || null);
            return true;
        } catch (acknowledgeError) {
            setError(acknowledgeError.message || 'Failed to acknowledge policy.');
            return false;
        } finally {
            setAcknowledging(false);
        }
    };

    return {
        status,
        warning,
        error,
        acknowledging,
        requiresAcknowledgement: Boolean(status?.requiresAcknowledgement && status?.activePolicy),
        acknowledge
    };
}
